import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Wallet } from "lucide-react";

import { api } from "@/lib/api";
import { CommissionItem } from "@/components/commission-item";
import { Header } from "@/components/header";
import { ChipRow, Empty, ErrorView, Loading } from "@/components/kit";
import { C, eur } from "@/lib/format";

function Tiers({ tiers }) {
  if (!tiers.data) return null;
  if (!tiers.data.custom)
    return <p data-testid="commissions-no-tiers" className="t-caption !text-[#F2994A]">Votre barème de commission n'a pas encore été défini par le gérant.</p>;
  return (
    <div data-testid="commissions-tiers" className="flex flex-col gap-1 rounded-xl bg-[#292415] p-3">
      <p className="t-label !text-[#D4AF37]">Votre barème de commission (selon montant signé)</p>
      {tiers.data.tiers.map((t, i) => (
        <p key={i} className="t-caption !text-[#E0E0E0]">
          {t.max ? `De ${eur(t.min)} à ${eur(t.max)}` : `À partir de ${eur(t.min)}`} : {String(t.percent).replace(".", ",")} %
        </p>
      ))}
    </div>
  );
}

export default function Commissions() {
  const [f, setF] = useState("all");
  const q = useQuery({ queryKey: ["agent-commissions"], queryFn: () => api("/agent/commissions") });
  const tiers = useQuery({ queryKey: ["public-settings"], queryFn: () => api("/settings/public") });
  const items = (q.data?.items ?? []).filter((c) => f === "all" || c.status === f);
  return (
    <div data-testid="commissions-screen" className="flex flex-col">
      <Header title="Mes commissions" />
      <div className="border-b border-[#222222] px-4 md:px-8">
        <ChipRow
          options={[{ key: "all", label: "Toutes" }, { key: "pending", label: "En attente" }, { key: "validated", label: "Validées" }, { key: "paid", label: "Payées" }]}
          value={f}
          onChange={setF}
          testIDPrefix="commissions-filter"
        />
      </div>
      {q.isLoading ? <Loading /> : q.isError || !q.data ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto grid w-full max-w-6xl gap-6 p-4 pb-8 md:p-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <div className="flex flex-col gap-3 lg:sticky lg:top-6 lg:self-start">
            <div className="grid grid-cols-3 gap-3">
              {[
                { l: "Gagné", v: q.data.totals.earned, c: C.brand, id: "earned" },
                { l: "En attente", v: q.data.totals.pending, c: C.warning, id: "pending" },
                { l: "Payé", v: q.data.totals.paid, c: C.success, id: "paid" },
              ].map((x) => (
                <div key={x.id} className="rounded-xl border border-[#333333] bg-[#1E1E1E] p-3">
                  <p className="t-caption">{x.l}</p>
                  <p data-testid={`commissions-total-${x.id}`} className="t-h3" style={{ color: x.c }}>{eur(x.v)}</p>
                </div>
              ))}
            </div>
            <p data-testid="commissions-remaining" className="t-body">
              Reste à percevoir : <span className="t-h3">{eur(q.data.totals.remaining)}</span>
            </p>
            <Tiers tiers={tiers} />
          </div>
          <div className="flex flex-col gap-3">
            {items.length ? items.map((c) => <CommissionItem key={c.id} c={c} />) : (
              <Empty icon={Wallet} title="Aucune commission" text="Vos commissions apparaissent dès qu'un contrat est signé." />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
